import { IconMail, IconMessage } from '@tabler/icons-vue';
import type { LeadTabItem } from '@/components/lead-design/LeadTabs.vue';
import translator from '@/locale/translator';
import type { FileRequestStatus, FormFileRequest, FormMessageMedium } from '@/types/candidate';
import type { FormContactMethod } from '@/types/publication';

// mirror the limits enforced by the PJAS backend
export const SMS_SIGNATURE_MAX_LENGTH = 320;
export const FILE_UPLOAD_MAX_COUNT = 20;
export const FILE_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;
export const FILE_UPLOAD_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
];

/** the backend lists messages newest first; a conversation reads oldest first */
export function sortOldestFirst<T extends { created_at: string }>(messages: T[]): T[] {
  return [...messages].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}

/**
 * the channels a candidate can be reached on, given the contact details we hold for them
 * and, when known, the contact method of the publication they applied through
 */
export function reachableMediums(
  candidate: { email?: string | null; phone?: string | null },
  contactMethod?: FormContactMethod | null
): FormMessageMedium[] {
  const mediums: FormMessageMedium[] = [];
  if (candidate.email?.trim() && contactMethod !== 'phone') mediums.push('email');
  if (candidate.phone?.trim() && contactMethod !== 'email') mediums.push('sms');
  return mediums;
}

/** keeps the medium while it is available, otherwise switches to the first one that is */
export function availableMediumOrFirst(
  medium: FormMessageMedium,
  availableMediums: FormMessageMedium[]
): FormMessageMedium {
  return availableMediums.includes(medium) ? medium : (availableMediums[0] ?? medium);
}

export function mediumTabs(availableMediums: FormMessageMedium[]): LeadTabItem<FormMessageMedium>[] {
  const tabs: LeadTabItem<FormMessageMedium>[] = [
    { value: 'email', label: translator.get('candidate.messaging.send_email'), icon: IconMail },
    { value: 'sms', label: translator.get('candidate.messaging.send_sms'), icon: IconMessage },
  ];
  return tabs.filter((tab) => availableMediums.includes(tab.value));
}

export function fileRequestStatus(
  fileRequest: Pick<FormFileRequest, 'fulfilled_at' | 'cancelled_at' | 'expires_at'>
): FileRequestStatus {
  if (fileRequest.fulfilled_at) return 'fulfilled';
  if (fileRequest.cancelled_at) return 'cancelled';
  if (new Date(fileRequest.expires_at) < new Date()) return 'expired';
  return 'pending';
}
